import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuthToken } from './useAuthToken';

const useAnalyticsData = (dateRange = '30daysAgo') => {
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [trafficSources, setTrafficSources] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [devices, setDevices] = useState([]);
  const [realtimeUsers, setRealtimeUsers] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { token } = useAuthToken();
  const realtimeInterval = useRef(null);

  const fetchAnalyticsData = async (signal) => {
    if (!token) return;

    try {
      setIsLoading(true);
      setError(null);

      // Get business_id from localStorage (following existing pattern)
      const businessId = localStorage.getItem('user_id');
      if (!businessId) {
        console.error('Business ID not found in localStorage');
        setError('Business ID not found. Please log in again.');
        setIsLoading(false);
        return;
      }

      // Parallel API calls for main analytics data
      const [summaryRes, trafficRes, pagesRes, devicesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_APIURL}analytics/summary?dateRange=${dateRange}&business_id=${businessId}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal
        }),
        axios.get(`${process.env.REACT_APP_APIURL}analytics/traffic-sources?dateRange=${dateRange}&business_id=${businessId}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal
        }),
        axios.get(`${process.env.REACT_APP_APIURL}analytics/top-pages?dateRange=${dateRange}&business_id=${businessId}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal
        }),
        axios.get(`${process.env.REACT_APP_APIURL}analytics/devices?dateRange=${dateRange}&business_id=${businessId}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal
        })
      ]);

      // Check if any response indicates disconnected
      const responses = [summaryRes, trafficRes, pagesRes, devicesRes];
      const disconnected = responses.some(res => 
        res.data.success && res.data.data && res.data.data.connected === false
      );

      if (disconnected) {
        setError('Analytics not connected');
        setIsLoading(false);
        return;
      }

      // Set data from responses
      if (summaryRes.data.success && summaryRes.data.data) {
        setSummary(summaryRes.data.data.summary || null);
        setChartData(summaryRes.data.data.chartData || []);
      }

      if (trafficRes.data.success && trafficRes.data.data) {
        setTrafficSources(trafficRes.data.data.sources || []);
      }

      if (pagesRes.data.success && pagesRes.data.data) {
        setTopPages(pagesRes.data.data.pages || []);
      }

      if (devicesRes.data.success && devicesRes.data.data) {
        setDevices(devicesRes.data.data.devices || []);
      }

      setIsLoading(false);
    } catch (err) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return; // Ignore cancelled requests
      }
      console.error('Analytics data error:', err);
      setError(err.response?.data?.message || 'Failed to fetch analytics data');
      setIsLoading(false);
    }
  };

  const fetchRealtimeUsers = async () => {
    if (!token) return;

    try {
      // Get business_id from localStorage (following existing pattern)
      const businessId = localStorage.getItem('user_id');
      if (!businessId) {
        console.error('Business ID not found in localStorage');
        return;
      }

      const response = await axios.get(`${process.env.REACT_APP_APIURL}analytics/realtime?business_id=${businessId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data) {
        if (response.data.data.connected === false) {
          return; // Skip if not connected
        }
        setRealtimeUsers(response.data.data.activeUsers || 0);
      }
    } catch (err) {
      console.error('Realtime data error:', err);
      // Don't set error for realtime failures, just log them
    }
  };

  useEffect(() => {
    if (!token) return; // Wait for token before doing anything
    
    const controller = new AbortController();
    const signal = controller.signal;
    
    fetchAnalyticsData(signal);
    
    // Cancel in-flight requests when dateRange changes
    return () => controller.abort();
  }, [token, dateRange]); // Depend on token and dateRange

  useEffect(() => {
    if (!token) return; // Wait for token before doing anything

    // Fetch realtime data immediately
    fetchRealtimeUsers();

    // Set up interval for realtime data (every 30 seconds)
    realtimeInterval.current = setInterval(fetchRealtimeUsers, 30000);

    // Cleanup interval on unmount
    return () => {
      if (realtimeInterval.current) {
        clearInterval(realtimeInterval.current);
      }
    };
  }, [token]); // Depend on token

  const refetch = () => {
    fetchAnalyticsData();
    fetchRealtimeUsers();
  };

  return {
    summary,
    chartData,
    trafficSources,
    topPages,
    devices,
    realtimeUsers,
    isLoading,
    error,
    refetch
  };
};

export default useAnalyticsData;